import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import ImageURL from '../../ImageURL';
import { getAdminToken } from '../adminSession';
import './AppManage.scss';
import './MainSlideAdmin.scss';

// 메인 슬라이드 관리 — 웹 메인·앱 홈 첫 영역 이미지 (server/routers/common/MainSlides.js, /mainslideadmin)
const api = axios.create({ baseURL: `${MainURL}/mainslideadmin` });
api.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) config.headers.set('X-Admin-Token', token);
  return config;
});
const err = (e: unknown, fallback = '처리하지 못했습니다.') =>
  (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error
  || (e as { response?: { data?: { message?: string } } })?.response?.data?.message
  || fallback;

type Slide = {
  id: number;
  title: string;
  image: string;
  imageMobile: string;
  linkUrl: string;
  showWeb: boolean;
  showApp: boolean;
  isActive: boolean;
};

type Form = {
  id: number | null;
  title: string;
  linkUrl: string;
  showWeb: boolean;
  showApp: boolean;
  isActive: boolean;
  image: File | null;
  imageMobile: File | null;
  removeImageMobile: boolean;
  current?: Slide;
};

const EMPTY_FORM: Form = {
  id: null,
  title: '',
  linkUrl: '',
  showWeb: true,
  showApp: true,
  isActive: true,
  image: null,
  imageMobile: null,
  removeImageMobile: false,
};

const slideSrc = (file: string) => `${ImageURL}/images/mainslide/${encodeURIComponent(file)}`;

function FilePreview({ file, fallback }: { file: File | null; fallback?: string }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!file) return setUrl('');
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  const src = url || (fallback ? slideSrc(fallback) : '');
  return src ? <img className="main-slide-admin__preview" src={src} alt="" /> : null;
}

export default function MainSlideAdmin() {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/list');
      setSlides(res.data?.items || []);
    } catch (e) {
      alert(err(e, '목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openEdit = (slide: Slide) =>
    setForm({ ...EMPTY_FORM, id: slide.id, title: slide.title, linkUrl: slide.linkUrl, showWeb: slide.showWeb, showApp: slide.showApp, isActive: slide.isActive, current: slide });

  const save = async () => {
    if (!form) return;
    if (!form.id && !form.image) return alert('PC 이미지를 선택해 주세요.');
    const link = form.linkUrl.trim();
    if (link && !(link.startsWith('/') || /^https?:\/\//i.test(link))) {
      return alert('링크는 /recruit 처럼 /로 시작하는 경로나 https:// 주소로 입력해 주세요.');
    }
    const body = new FormData();
    body.append('title', form.title);
    body.append('linkUrl', link);
    body.append('showWeb', form.showWeb ? '1' : '0');
    body.append('showApp', form.showApp ? '1' : '0');
    body.append('isActive', form.isActive ? '1' : '0');
    if (form.removeImageMobile) body.append('removeImageMobile', '1');
    if (form.image) body.append('image', form.image);
    if (form.imageMobile) body.append('imageMobile', form.imageMobile);
    setSaving(true);
    try {
      if (form.id) await api.put(`/${form.id}`, body);
      else await api.post('/', body);
      setForm(null);
      await load();
    } catch (e) {
      alert(err(e, '저장하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slide: Slide) => {
    if (!window.confirm(`'${slide.title || '이름 없음'}' 슬라이드를 삭제할까요? 이미지 파일도 함께 지워집니다.`)) return;
    try {
      await api.delete(`/${slide.id}`);
      await load();
    } catch (e) {
      alert(err(e, '삭제하지 못했습니다.'));
    }
  };

  const toggleActive = async (slide: Slide) => {
    try {
      const body = new FormData();
      body.append('isActive', slide.isActive ? '0' : '1');
      await api.put(`/${slide.id}`, body);
      await load();
    } catch (e) {
      alert(err(e));
    }
  };

  const move = async (index: number, delta: number) => {
    const next = [...slides];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setSlides(next);
    try {
      await api.patch('/order', { ids: next.map((s) => s.id) });
    } catch (e) {
      alert(err(e, '순서를 저장하지 못했습니다.'));
      await load();
    }
  };

  return (
    <div className="admin-board-manage admin-app-manage main-slide-admin">
      <header className="admin-app-manage__head">
        <h2 className="admin-app-manage__title">메인 슬라이드</h2>
        <p className="admin-app-manage__desc">
          웹 메인과 앱 홈 화면 맨 위에 나오는 슬라이드 이미지를 관리합니다. 5초마다 자동으로 넘어가며, 노출 중인 슬라이드가 없으면 영역이 표시되지 않습니다.
        </p>
      </header>

      <div className="main-slide-admin__toolbar">
        <button type="button" className="is-primary" onClick={() => setForm({ ...EMPTY_FORM })}>+ 슬라이드 추가</button>
        <span>PC 이미지 권장 2800×1120 (5:2) · 모바일/앱 이미지 권장 1080×540 (2:1) · 10MB 이하 jpg, png, gif, webp</span>
      </div>

      {form && (
        <div className="main-slide-admin__form">
          <h3>{form.id ? '슬라이드 수정' : '새 슬라이드'}</h3>
          <label>
            <span>이름 (대체 텍스트)</span>
            <input value={form.title} maxLength={100} placeholder="예: 2026 가을 청빙 박람회" onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label>
            <span>링크 (선택)</span>
            <input value={form.linkUrl} maxLength={500} placeholder="/recruit 또는 https://..." onChange={(e) => setForm({ ...form, linkUrl: e.target.value })} />
            <small>/로 시작하면 사이트 안 페이지로 이동 (앱에서는 브라우저로 열림), https:// 주소는 새 창으로 열립니다. 비우면 클릭해도 이동하지 않습니다.</small>
          </label>
          <div className="main-slide-admin__images">
            <label>
              <span>PC 이미지 {form.id ? '(바꿀 때만 선택)' : '(필수)'}</span>
              <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={(e) => setForm({ ...form, image: e.target.files?.[0] || null })} />
              <FilePreview file={form.image} fallback={form.current?.image} />
            </label>
            <label>
              <span>모바일·앱 이미지 (선택 — 없으면 PC 이미지를 잘라서 사용)</span>
              <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={(e) => setForm({ ...form, imageMobile: e.target.files?.[0] || null, removeImageMobile: false })} />
              <FilePreview file={form.imageMobile} fallback={form.removeImageMobile ? '' : form.current?.imageMobile} />
              {form.current?.imageMobile && !form.imageMobile && !form.removeImageMobile && (
                <button type="button" onClick={() => setForm({ ...form, removeImageMobile: true })}>모바일 이미지 삭제</button>
              )}
            </label>
          </div>
          <div className="main-slide-admin__checks">
            <label><input type="checkbox" checked={form.showWeb} onChange={(e) => setForm({ ...form, showWeb: e.target.checked })} /> 웹에 노출</label>
            <label><input type="checkbox" checked={form.showApp} onChange={(e) => setForm({ ...form, showApp: e.target.checked })} /> 앱에 노출</label>
            <label><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> 사용</label>
          </div>
          <div className="main-slide-admin__actions">
            <button type="button" className="is-primary" disabled={saving} onClick={save}>{saving ? '저장 중…' : '저장'}</button>
            <button type="button" disabled={saving} onClick={() => setForm(null)}>취소</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="main-slide-admin__empty">불러오는 중…</p>
      ) : slides.length === 0 ? (
        <p className="main-slide-admin__empty">등록된 슬라이드가 없습니다.</p>
      ) : (
        <table className="main-slide-admin__table">
          <thead>
            <tr>
              <th>순서</th>
              <th>이미지</th>
              <th>이름 · 링크</th>
              <th>노출</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {slides.map((slide, i) => (
              <tr key={slide.id} className={slide.isActive ? '' : 'is-off'}>
                <td>
                  <div className="main-slide-admin__order">
                    <button type="button" disabled={i === 0} onClick={() => move(i, -1)} aria-label="위로">▲</button>
                    <span>{i + 1}</span>
                    <button type="button" disabled={i === slides.length - 1} onClick={() => move(i, 1)} aria-label="아래로">▼</button>
                  </div>
                </td>
                <td>
                  <div className="main-slide-admin__thumbs">
                    <img src={slideSrc(slide.image)} alt="" />
                    {slide.imageMobile && <img className="is-mobile" src={slideSrc(slide.imageMobile)} alt="" />}
                  </div>
                </td>
                <td>
                  <strong>{slide.title || '(이름 없음)'}</strong>
                  <div className="main-slide-admin__link">{slide.linkUrl || '링크 없음'}</div>
                </td>
                <td>
                  {[slide.showWeb && '웹', slide.showApp && '앱'].filter(Boolean).join(' · ') || '없음'}
                  <div>
                    <button type="button" className={slide.isActive ? 'is-on' : ''} onClick={() => toggleActive(slide)}>
                      {slide.isActive ? '사용 중' : '숨김'}
                    </button>
                  </div>
                </td>
                <td>
                  <div className="main-slide-admin__actions">
                    <button type="button" onClick={() => openEdit(slide)}>수정</button>
                    <button type="button" onClick={() => remove(slide)}>삭제</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
